/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import TextBlock from '@component/common/block/text-block';
import Skeleton from '@design-system/skeleton';

interface IProps {
  ratio: number;
  theme?: ETheme;
  width?: string;
  height?: string;
  text?: string;
  loading?: boolean;
}

const createDesign = (theme: ETheme, width: string | undefined, height: string | undefined) => {
  // ############### createTheme ###############
  const createTheme = () => {
    switch (theme) {
      case ETheme.BLACK:
        return { fillColor: color.BLACK_2 };
      case ETheme.BROWN:
        return { fillColor: color.BROWN_2 };
      case ETheme.RED:
        return { fillColor: color.RED_2 };
      case ETheme.BLUE:
        return { fillColor: color.BLUE_2 };
      case ETheme.GREEN:
        return { fillColor: color.GREEN_3 };
      case ETheme.GRAY:
        return { fillColor: color.GRAY_1 };
      case ETheme.YELLOW:
      default:
        return { fillColor: color.YELLOW_2 };
    }
  };

  // ############### Combine ###############
  const themeDesign = createTheme();

  return {
    width,
    height,
    fillColor: themeDesign.fillColor,
    trackColor: color.GRAY_3,
    borderRadius: size.BORDER_RADIUS_1,
  };
};

interface ITotalProgressBarProps {
  $design: ReturnType<typeof createDesign>;
}

const Wrapper = styled.div<ITotalProgressBarProps>`
  position: relative;
  width: ${(props) => props.$design.width || '100%'};
  height: ${(props) => props.$design.height || '10px'};
  background-color: ${(props) => props.$design.trackColor};
  border-radius: ${(props) => props.$design.borderRadius};
  overflow: hidden;
`;

const Current = styled.div<{ $design: ReturnType<typeof createDesign>; $ratio: number }>`
  height: 100%;
  width: ${(props) => `${Math.min(100, Math.max(0, props.$ratio * 100))}%`};
  background-color: ${(props) => props.$design.fillColor};
  border-radius: ${(props) => props.$design.borderRadius};
  transition: width 0.3s ease;
`;

const ProgressBar: React.FC<IProps> = ({ ratio, theme = ETheme.YELLOW, width, height, text, loading = false }) => {
  const design = createDesign(theme, width, height);

  return (
    <Wrapper $design={design}>
      {loading ? (
        <Skeleton width='100%' height='100%' borderRadius={design.borderRadius} />
      ) : (
        <>
          <Current $design={design} $ratio={ratio} />
          {text && (
            <TextBlock
              text={text}
              design={{
                text: { size: 'tiny', color: color.GRAY_1, bold: true },
                box: { top: '0', left: '0', width: '100%', height: '100%', alignX: 'center', alignY: 'center' },
              }}
            />
          )}
        </>
      )}
    </Wrapper>
  );
};

export default ProgressBar;
